import { useState, useRef } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import Reveal from "./Reveal";
import { CheckCircle2, Loader2, ArrowRight, Upload, FileText, X } from "lucide-react";

const DRIVER_IMG =
  "https://images.pexels.com/photos/14797994/pexels-photo-14797994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=900";

const initial = { full_name: "", email: "", phone: "", city: "", state: "", cdl_experience: "", endorsements: "", message: "" };

const inputCls =
  "w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-ksy-ink placeholder-slate-400 focus:border-ksy-blue focus:outline-none focus:ring-2 focus:ring-blue-100 transition-colors";
const labelCls = "block text-sm font-semibold text-slate-700 mb-1.5";

export default function DriverForm() {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const exp = t("drivers.exp_opt");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const okExt = /\.(jpe?g|png|webp|pdf)$/i.test(f.name);
    if (!okExt) { toast.error(t("drivers.file_type_error")); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error(t("drivers.file_size_error")); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let license_path = null, license_filename = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/upload-license", fd, { headers: { "Content-Type": "multipart/form-data" } });
        license_path = up.data.path;
        license_filename = up.data.filename;
      }
      await api.post("/drivers", { ...form, license_path, license_filename });
      toast.success(t("drivers.success"));
      setForm(initial);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("drivers.error"));
    } finally {
      setLoading(false);
    }
  };

  const benefits = [t("drivers.b1"), t("drivers.b2"), t("drivers.b3"), t("drivers.b4")];

  return (
    <section id="drivers" className="py-20 md:py-28 bg-ksy-bg" data-testid="drivers-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <span className="text-sm font-bold uppercase tracking-[0.18em] text-ksy-blue">{t("drivers.label")}</span>
          <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-ksy-navy tracking-tight">{t("drivers.title")}</h2>
          <p className="mt-4 text-lg text-slate-500 leading-relaxed">{t("drivers.subtitle")}</p>
          <ul className="mt-8 space-y-3">
            {benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-ksy-navy font-semibold">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" /> {b}
              </li>
            ))}
          </ul>
          <div className="mt-8 rounded-2xl overflow-hidden shadow-lg ring-1 ring-slate-200">
            <img src={DRIVER_IMG} alt="Professional KS&Y truck driver" className="w-full h-64 object-cover object-top" />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-8" data-testid="driver-form">
            <h3 className="font-display font-bold text-xl text-ksy-navy">{t("drivers.form_title")}</h3>
            <p className="text-sm text-slate-500 mt-1 mb-6">{t("drivers.form_subtitle")}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className={labelCls}>{t("drivers.name")}</label><input required value={form.full_name} onChange={set("full_name")} className={inputCls} data-testid="driver-name" /></div>
              <div><label className={labelCls}>{t("drivers.email")}</label><input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="driver-email" /></div>
              <div><label className={labelCls}>{t("drivers.phone")}</label><input required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="driver-phone" /></div>
              <div><label className={labelCls}>{t("drivers.city")}</label><input value={form.city} onChange={set("city")} className={inputCls} data-testid="driver-city" /></div>
              <div><label className={labelCls}>{t("drivers.state")}</label><input value={form.state} onChange={set("state")} className={inputCls} data-testid="driver-state" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>{t("drivers.experience")}</label>
                <select required value={form.cdl_experience} onChange={set("cdl_experience")} className={inputCls} data-testid="driver-experience">
                  <option value="" disabled>—</option>
                  {exp.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2"><label className={labelCls}>{t("drivers.endorsements")}</label><input value={form.endorsements} onChange={set("endorsements")} className={inputCls} data-testid="driver-endorsements" /></div>
              <div className="sm:col-span-2">
                <label className={labelCls}>{t("drivers.license")}</label>
                {!file ? (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg px-4 py-4 text-slate-500 hover:border-ksy-blue hover:text-ksy-blue transition-colors"
                    data-testid="driver-license-trigger"
                  >
                    <Upload className="w-5 h-5" /> {t("drivers.license_cta")}
                  </button>
                ) : (
                  <div className="w-full flex items-center gap-3 border border-slate-300 rounded-lg px-4 py-3 bg-slate-50" data-testid="driver-license-selected">
                    <FileText className="w-5 h-5 text-ksy-blue shrink-0" />
                    <span className="text-sm text-ksy-navy truncate flex-1">{file.name}</span>
                    <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-slate-400 hover:text-red-500" data-testid="driver-license-remove"><X className="w-4 h-4" /></button>
                  </div>
                )}
                <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={onFile} className="hidden" data-testid="driver-license-input" />
                <p className="text-xs text-slate-400 mt-1.5">{t("drivers.license_hint")}</p>
              </div>
              <div className="sm:col-span-2"><label className={labelCls}>{t("drivers.message")}</label><textarea rows={3} value={form.message} onChange={set("message")} className={inputCls} data-testid="driver-message" /></div>
            </div>
            <button type="submit" disabled={loading} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-ksy-navy text-white font-bold px-6 py-3.5 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-60" data-testid="driver-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {loading ? t("drivers.submitting") : t("drivers.submit")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
